/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {
  bulkUpdateUsers,
  deleteUser as destroyUser,
  deleteUsers as destroyUsers,
  getAccountRoles,
  getUser,
  getUserExtendedAttributes,
  loadUsers,
  saveUserExtendedAttributes
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  createUser,
  updateUser,
  updateUsers,
  UserContext
} from '@ion/core/src/services/MicroServices';
import {
  AuditChange,
  EnhancedUser,
  EnhancedUsers,
  Entity,
  ExtendedAttribute,
  GenericObject,
  Role,
  User,
  UserFilters,
  Users
} from '@ion/core/src/types/entities';
import {LoadUsersParams, ServiceError} from '@ion/core/src/types/services';
import {contains} from '@ion/core/src/utils/StringUtil';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {
  dehydrateCurrentUser,
  hydrateCurrentUser
} from '@ion/query/src/hooks/useCurrentUser';
import {retryUpdate} from '@ion/services/src/helpers/retry';
import {
  AdminAction,
  AsyncState,
  PageInitializeAction
} from '@/types/redux';
import {
  getAccountId,
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../../../helpers';
import {
  errorAddOptions,
  errorEditOptions,
  errorOptions,
  successAddOptions,
  successEditOptions
} from '../../../prompts/constants';
import {importConfirm} from '../../../store/actions/importer';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import {PageState} from '../../../types/pages';
import {gridDefaultPageSize} from '../../../utils/helpers';
import {getCurrentUser} from './useAuthenticatedUser';

// name of the reducer
export const NAME                       = 'adminUsers';

/**
 * ACTIONS
 */
export const SET_ADMIN_USERS_PARAMS = 'SET_ADMIN_USERS_PARAMS';

// initialize users ...
export const PRE_INITIALIZE_ADMIN_USERS = 'PRE_INITIALIZE_ADMIN_USERS';
export const INITIALIZE_ADMIN_USERS     = 'INITIALIZE_ADMIN_USERS';

// grid column state
export const SET_GRID_COLUMNS           = 'SET_ADMIN_USERS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS        = 'UPDATE_ADMIN_USERS_GRID_COLUMNS';

export const ADMIN_USERS_GRID_REFRESHED = 'ADMIN_USERS_GRID_REFRESHED';

// grid sub groups
export const SET_VIEW_SUB_GROUPS        = 'SET_ADMIN_USERS_VIEW_SUB_GROUPS';

export const SET_GRID_PAGE_SIZE         = 'SET_ADMIN_USERS_GRID_PAGE_SIZE';

// users retrieval ...
export const GET_ADMIN_USERS_REQUEST    = 'GET_ADMIN_USERS_REQUEST';
export const GET_ADMIN_USERS_RESPONSE   = 'GET_ADMIN_USERS_RESPONSE';
export const GET_ADMIN_USERS_ERROR      = 'GET_ADMIN_USERS_ERROR';

// user bulk import ...
export const IMPORT_ADMIN_USERS         = 'IMPORT_ADMIN_USERS';

// user add ...
export const ADD_ADMIN_USER             = 'ADD_ADMIN_USER';
export const ADD_ADMIN_USER_REQUEST     = 'ADD_ADMIN_USER_REQUEST';
export const ADD_ADMIN_USER_RESPONSE    = 'ADD_ADMIN_USER_RESPONSE';
export const ADD_ADMIN_USER_ERROR       = 'ADD_ADMIN_USER_ERROR';

// user edit ...
export const EDIT_ADMIN_USER            = 'EDIT_ADMIN_USER';
export const EDIT_ADMIN_USER_REQUEST    = 'EDIT_ADMIN_USER_REQUEST';
export const EDIT_ADMIN_USER_RESPONSE   = 'EDIT_ADMIN_USER_RESPONSE';
export const EDIT_ADMIN_USER_ERROR      = 'EDIT_ADMIN_USER_ERROR';

// user delete ...
export const DELETE_ADMIN_USER          = 'DELETE_ADMIN_USER';
export const DELETE_ADMIN_USER_REQUEST  = 'DELETE_ADMIN_USER_REQUEST';
export const DELETE_ADMIN_USER_RESPONSE = 'DELETE_ADMIN_USER_RESPONSE';
export const DELETE_ADMIN_USER_ERROR    = 'DELETE_ADMIN_USER_ERROR';

// view user audit log ...
export const AUDIT_ADMIN_USER                     = 'AUDIT_ADMIN_USER';
export const AUDIT_ADMIN_USER_REQUEST             = 'AUDIT_ADMIN_USER_REQUEST';
export const AUDIT_ADMIN_USER_RESPONSE            = 'AUDIT_ADMIN_USER_RESPONSE';
export const AUDIT_ADMIN_USER_ERROR               = 'AUDIT_ADMIN_USER_ERROR';
export const AUDIT_ADMIN_USER_SHOW_CHANGES_DIALOG = 'AUDIT_ADMIN_USER_SHOW_CHANGES_DIALOG';

// bulk user edits ...
export const EDIT_ADMIN_USERS            = 'EDIT_ADMIN_USERS';
export const EDIT_ADMIN_USERS_REQUEST    = 'EDIT_ADMIN_USERS_REQUEST';
export const EDIT_ADMIN_USERS_RESPONSE   = 'EDIT_ADMIN_USERS_RESPONSE';
export const EDIT_ADMIN_USERS_ERROR      = 'EDIT_ADMIN_USERS_ERROR';

// bulk user moves ...
export const MOVE_ADMIN_USERS            = 'MOVE_ADMIN_USERS';
export const MOVE_ADMIN_USERS_REQUEST    = 'MOVE_ADMIN_USERS_REQUEST';
export const MOVE_ADMIN_USERS_RESPONSE   = 'MOVE_ADMIN_USERS_RESPONSE';
export const MOVE_ADMIN_USERS_ERROR      = 'MOVE_ADMIN_USERS_ERROR';
export const SHOW_MOVED_USERS            = 'SHOW_MOVED_USERS';

// bulk user deletes ...
export const DELETE_ADMIN_USERS          = 'DELETE_ADMIN_USERS';
export const DELETE_ADMIN_USERS_REQUEST  = 'DELETE_ADMIN_USERS_REQUEST';
export const DELETE_ADMIN_USERS_RESPONSE = 'DELETE_ADMIN_USERS_RESPONSE';
export const DELETE_ADMIN_USERS_ERROR    = 'DELETE_ADMIN_USERS_ERROR';

//Modular Search
export const UPDATE_USERS_SEARCH_QUERIES = 'UPDATE_USERS_SEARCH_QUERIES';
export const RESET_USERS_SEARCH_QUERIES  = 'RESET_USERS_SEARCH_QUERIES';

// update the current user
export const UPDATE_CURRENT_USER         = 'UPDATE_CURRENT_USER';

// User change password ...
export const USER_PASSWORD_CHANGE        = 'USER_PASSWORD_CHANGE';

// User save password ...
export const USER_PASSWORD_SAVE          = 'USER_PASSWORD_SAVE';

/**
 * action that sets the pathname and query in the store
 */
export const setAdminUsersParams = ({pathname}) => ({
  type : SET_ADMIN_USERS_PARAMS, pathname
});
/**
 * @initialize
 *
 * In order to ensure the user profile editor has been initialized, all iOn application
 * roles must be retrieved prior to enabling of the user editor (i.e. user profile).
 */
export const preinitializeAdminUsers = () => {
  return async(dispatch: Function, getState: Function): Promise<void> => {
    try {
      const user      = getCurrentUser(getState);
      const accountId = entityId(user.account);
      const roles     = await getAccountRoles(accountId);
      dispatch({type: PRE_INITIALIZE_ADMIN_USERS, roles, accountId});
    } catch (err) {
      dispatch({type: PRE_INITIALIZE_ADMIN_USERS});
      error(err);
    }
  };
};

/**
 * @initialize
 */
export const initializeAdminUsers = () => ({
  type : INITIALIZE_ADMIN_USERS
});

/**
 * @adminUsersGridRefreshed
 */
export const adminUsersGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_USERS_GRID_REFRESHED};
};

/**
 * @getAdminUsers
 */
export const getAdminUsers = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  includeGlobalSearch = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_USERS_REQUEST});
    const {
      adminUsers: {
        search,
        rolesByAccountId: cachedRolesByAccountId
      }
    } = getState();
    const data: LoadUsersParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups,
      includeGlobalSearch
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    data.filterOn = {
      ...data.filterOn,
      ...(!includeGlobalSearch && createModularSearchFilters(search))
    };
    try {
      const {users: items, totalCount} = await loadUsers(data);
      const preloadedRoles: Role[]  = getState().adminUsers.roles ?? [];
      const uniqueAccountIds: number[] = Array.from(new Set(
        items.map((u: User) => entityId(u.account))
      ));
      const uncachedAccountIds: number[] = uniqueAccountIds.filter(
        (id: number) => !cachedRolesByAccountId?.[id]
      );
      const settledRoles = await Promise.allSettled(
        uncachedAccountIds.map((id: number) => getAccountRoles(id))
      );
      const fetchedRolesByAccountId: Record<number, Role[]> = {};
      settledRoles.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          fetchedRolesByAccountId[uncachedAccountIds[index]] = result.value;
        }
      });
      const cachedRoles: Role[] = uniqueAccountIds
        .filter((id: number) => cachedRolesByAccountId?.[id])
        .flatMap((id: number) => cachedRolesByAccountId[id]);
      const additionalRoles: Role[] = Object.values(fetchedRolesByAccountId).flat();
      const roles = [...preloadedRoles, ...cachedRoles, ...additionalRoles];
      dispatch({type: GET_ADMIN_USERS_RESPONSE, rolesByAccountId: fetchedRolesByAccountId});
      callbackFn?.({items, totalCount}, roles);
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_USERS_ERROR, error: err});
      return err;
    }
  };
};

/**
 * Invoked when the Bulk Import Admin User Dialog is to be displayed or removed.
 */
export const importAdminUsers = (show = true, result = null) => {
  return async(dispatch: Function) => {
    dispatch(importConfirm(Entity.user));
    dispatch({type: IMPORT_ADMIN_USERS, show, result});
  };
};

/**
 * @addAdminUser
 *
 * Invoked when the Add Admin User editor is to be displayed or removed.
 */
export const addAdminUser = (show = true) => ({
  type : ADD_ADMIN_USER, show
});

/**
 *
 * Invoked when a new user is to be created; consolidating the process of User
 * creation into a single transaction. User creation is proxied through the
 * MicroServices which manage sending out the new user confirmation emails,
 * etc.
 *
 */
export const addUser = (user: EnhancedUser, context?: UserContext): Function => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: ADD_ADMIN_USER_REQUEST, user});
    try {
      const created = await createUser(user, context);
      dispatch({type: ADD_ADMIN_USER_RESPONSE, user: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.user)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_USER_ERROR, error: err});
      if (contains(err?.errorText, 'Exception$NotFound')) {
        userError(dispatch);
      } else {
        dispatch(toggleUserPrompt(errorAddOptions(Entity.user, err)));
      }
      error(err);
    }
  };
};

/**
 * @editAdminUser
 *
 * Invoked when the Edit Admin User editor is to be displayed or removed. If the user
 * being edited is the current user (i.e. the logged in user, pass the isCurrentUser
 * argument as true).
 */
export const editAdminUser = (user: User, show = true, isView) => {
  return async(dispatch: Function, getState: Function): Promise<void> => {
    const id = dehydrateCurrentUser()?.id || getState().login?.user?.id;
    const isCurrent = user.id === id;
    dispatch({type: EDIT_ADMIN_USER, user, show, isCurrentUser: isCurrent, isView});
  };
};

/**
 * @editUser
 *
 * Invoked when an existing user is to be updated; consolidating the process of
 * User updates into a single transaction. The process for editing a User is
 * outlined below.
 *
 * 1.) Update the User against CTC Services (proxying through the MicroServices
 *     so as to facilitate the new email confirmation email).
 * 2). Update User ExtendedAttributes and / or Preferences:
 *     2.1) If the user is not the current user, update their extendedAttributes.
 *          For more information, see the updateUserExtendedAttributes function.
 *
 */
export const editUser = (user: EnhancedUser, isCurrentUser?: boolean, context?: UserContext): Function => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_USER_REQUEST});
    try {
      const updated = await retryUpdate({
        newEntity    : user,
        updateEntity : updateUser,
        getEntity    : getSingleUser,
        onError      : userUpdateError,
        isUser       : true,
        dispatch,
        context
      });
      if (updated) {
        if (!isCurrentUser) {
          if (user.extendedAttribute) {
            updateUserExtendedAttributes(updated.id, user.extendedAttribute);
          }
        } else {
          dispatch({type: UPDATE_CURRENT_USER, updated});
          hydrateCurrentUser(updated);
          UserPreferences.persistDefaultLanguage();
          if (!UserPreferences.hasChangeDetection()) {
            UserPreferences.save();
          }
        }
        dispatch({type: EDIT_ADMIN_USER_RESPONSE, user: updated, isCurrentUser});
        dispatch(toggleUserPrompt(successEditOptions(Entity.user)));
      }
    } catch (err) {
      if (contains(err?.errorText, 'Exception$NotFound')) {
        userError(dispatch);
      } else {
        userUpdateError(err, dispatch);
      }
    }
  };
};

/**
 * Invoked when an existing User gets resource out of state, delegating to the fetch
 * latest user details successful response or error action accordingly.
 */
const getSingleUser = async(roleID: number) => {
  try {
    const user = await getUser(roleID);
    return {updated: user, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing User gets updated or fetched, CTC Services
 * and dispatching a successful or error action accordingly.
 */
const userUpdateError = (err, dispatch: Function) => {
  dispatch({type: EDIT_ADMIN_USER_ERROR, error: err});
  if (contains(err?.errorText, 'Unauthorized Updating:')) {
    dispatch(toggleUserPrompt({
      ...errorOptions,
      actionLabel : translate('common:dismiss'),
      message     : err?.errorText?.replaceAll(/[:]/g, ' ')
    }));
  } else {
    dispatch(toggleUserPrompt(errorEditOptions(Entity.user, err)));
  }
  error(err);
};

/**
 * Invoked when the service returns non relevant error message.
 */
const userError = (dispatch: Function) => {
  dispatch(toggleUserPrompt({
    ...errorOptions,
    actionLabel : translate('common:dismiss'),
    message     : translate('admin-users:toggleUserPrompt.unableToCreateUser')
  }));
};

/**
 * @editAdminUsers
 *
 * Invoked when the Bulk Edit Admin Users Dialog is to be displayed or removed.
 */
export const editAdminUsers = (users: EnhancedUsers, show = true) => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: EDIT_ADMIN_USERS, users, show});
  };
};

/**
*
* Invoked when the User Change password is to be displayed or removed. If the user
* logins into the application and has a key request to change his password then pass show
* argument as true.
*/
export const changePassword = (show = false) => ({
  type : USER_PASSWORD_CHANGE, show
});

/**
*
* Invoked when the User password needs to be changed to save the current password on login.
* Will use the save password during change password API.
*/
export const savePassword = (userPassword: string = '') => ({
  type : USER_PASSWORD_SAVE, userPassword
});

/**
 * @editUsers
 *
 * Invoked when a collection of existing users are to be updated; consolidating the process of
 * Users updates into a single transaction. Update the List of Users against CTC Services
 * (proxying through the MicroServices so as to facilitate the new email confirmation email).
 */
export const editUsers = (users: EnhancedUsers, context: UserContext): Function => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_USERS_REQUEST});
    try {
      const {results, errors} = await updateUsers(users, context);
      dispatch({type: EDIT_ADMIN_USERS_RESPONSE, users: results});
      dispatch(toggleUserPrompt(
        successEditOptions(
          translate('common:entityLabels.users').toLowerCase(),
          {results, items: users})
      ));
    } catch (err) {
      dispatch({type: EDIT_ADMIN_USERS_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(translate('common:entityLabels.users'))));
      error(err);
    }
  };
};

/**
 * Called to update the modular search queries to the assets state
 */
export const updateUsersSearchQueries = (searchParams: UserFilters) => ({
  type : UPDATE_USERS_SEARCH_QUERIES, searchParams
});

/**
 * Called to Reset the assets filters state
 */
export const resetUsersSearchQueries = () => ({
  type : RESET_USERS_SEARCH_QUERIES
});
/**
 * @auditAdminUser
 *
 * Invoked when the the user auditor is to be displayed or removed.
 */
export const auditAdminUser = (user: User, show = true) => ({
  type : AUDIT_ADMIN_USER, user, show
});

export const showAuditChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_ADMIN_USER_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * @deleteAdminUser
 *
 * Invoked when the Delete Admin User Dialog is to be displayed or removed.
 */
export const deleteAdminUser = (user: User, show = true) => ({
  type : DELETE_ADMIN_USER, user, show
});

/**
 * @deleteUser
 *
 * Invoked when an existing user is to be deleted, delegating to the deleteUser
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteUser = (user: EnhancedUser) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_USER_REQUEST});
    try {
      const deleted = await destroyUser(user);
      dispatch({type: DELETE_ADMIN_USER_RESPONSE, user: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_USER_ERROR, error: err});
      error(err);
    }
  };
};

/**
 *
 * Invoked when the Bulk Move Admin User editor is to be displayed or removed.
 */
export const moveAdminUsers = (users: User[], show = true) => ({
  type : MOVE_ADMIN_USERS, users, show
});

/**
 *
 * Invoked when a collection of existing users are to be updated, delegating to
 * the updateUsers CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const moveUsers = (users: User[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: MOVE_ADMIN_USERS_REQUEST});
    try {
      const {results} = await bulkUpdateUsers(users);
      dispatch({type: MOVE_ADMIN_USERS_RESPONSE, users: results});
    } catch (err) {
      dispatch({type: MOVE_ADMIN_USERS_ERROR, error: err});
      error(err);
    }
  };
};

/**
 *
 * Invoked when the Moved users grid is to be displayed or removed inside dialog.
 */
export const showMovedUsers = (show = true) => ({
  type : SHOW_MOVED_USERS, show
});

/**
 * @updateUserExtendedAttributes
 *
 * Provides a utility which manages setting or resetting the user's extendedAttributes.
 *
 * @param   {number} id The id of the user for which extendedAttributes are to be
 *          updated or created.
 * @param   {Promise<ExtendedAttribute>} A Promise which always resolves the current
 *          ExtendedAttribute for the user.
 */
const updateUserExtendedAttributes = async(id: number, {value}: ExtendedAttribute = {value: null}) => {
  try {
    const current = await getUserExtendedAttributes(id);
    if (current.value) {
      if (value) {
        const {fleetOutlook} = value as GenericObject;
        current.value.fleetOutlook = fleetOutlook;
      } else {
        delete current.value?.fleetOutlook;
      }
      saveUserExtendedAttributes(id, current.value);
    } else {
      saveUserExtendedAttributes(id, value, 'post');
    }
  } catch (err) {
    error(err);
  }
};
/**
 * @deleteAdminUsers
 *
 * Invoked when the Bulk Delete Admin Users Dialog is to be displayed or removed.
 */
export const deleteAdminUsers = (users: Users, show = true) => ({
  type : DELETE_ADMIN_USERS, users, show
});

/**
 * @deleteUsers
 *
 * Invoked when a collection of existing users are to be deleted, delegating to
 * the destroyUsers CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteUsers = (users: Users) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_USERS_REQUEST});
    try {
      const {results, errors} = await destroyUsers(users);
      dispatch({type: DELETE_ADMIN_USERS_RESPONSE, users: results});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_USERS_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setUserViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user change the rows per page dropdown
 */
export const setUsersGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

/**
 * REDUCERS
 */

export interface AdminUsersAction extends AdminAction {
  user?: User;
  users?: User[];
  errors?: ServiceError[];
  roles?: Role[];
  accountId?: number;
  rolesByAccountId?: Record<number, Role[]>;
};

export interface AdminUsersEditAction extends AdminAction {
  user?: User;
  users?: User[];
  isCurrentUser: boolean;
  isView: boolean;
};

export interface AdminUsersActionState extends AsyncState {
  selected: User | User[] | null;
  result: User | User[] | null;
  isEditing: boolean;
  record?: User | User[] | null;
  isView: boolean;
  viewMovedUsers?: boolean;
  errors?: ServiceError[];
};

export interface ChangePasswordState {
  showDialog?: boolean;
}

export interface SavePasswordState {
  userPassword: string;
}

export interface AdminUsersAuditState extends AdminUsersActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
}

export interface AdminUsersImportState {
  isImporting: boolean;
  result: User | User[] | null;
};

export interface AdminUsersEditActionState extends AdminUsersActionState {
  isCurrentUser: boolean;
}

// defines the user preference key for the admin users page ...
export const PREFERENCE = 'admin-users';

export interface AdminUsersState extends PageState {
  pathname: string;
  isPreInitialized: boolean;
  users: {
    error: ServiceError;
    loading: boolean;
  },
  roles: Role[],
  rolesByAccountId: Record<number, Role[]>,
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number
  },
  add: AdminUsersActionState,
  edit: AdminUsersEditActionState,
  audit: AdminUsersAuditState,
  destroy: AdminUsersActionState,
  changePassword: ChangePasswordState,
  savePassword: SavePasswordState,
  bulkEdit: AdminUsersActionState,
  bulkDelete: AdminUsersActionState,
  bulkImport: AdminUsersImportState,
  search: UserFilters,
  move: AdminUsersActionState,
}

// defines the initial state for the reducer ...
export const initialState: AdminUsersState = {
  pathname         : null,
  isPreInitialized : false,
  isInitialized    : false,
  users            : {
    error   : null,
    loading : false
  },
  roles            : null,
  rolesByAccountId : {},
  grid             : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  edit : {
    isEditing     : false,
    selected      : null,
    loading       : false,
    error         : null,
    result        : null,
    isCurrentUser : false,
    isView        : false
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  changePassword : {
    showDialog : false
  },
  savePassword : {
    userPassword : null
  },
  audit : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    isView            : false,
    showChangesDialog : false,
    auditRecord       : null
  },
  bulkEdit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    record    : null,
    isView    : false
  },
  bulkImport : {
    isImporting : false,
    result      : null
  },
  search : {
    status      : null,
    id          : null,
    'role.name' : null,
    username    : null,
    firstName   : null,
    lastName    : null,
    description : null
  },
  move : {
    isEditing      : false,
    selected       : null,
    loading        : false,
    error          : null,
    result         : null,
    errors         : null,
    viewMovedUsers : false,
    isView         : false
  }
};

// defines the reducers ...
const reducers = {
  [SET_ADMIN_USERS_PARAMS] : (draft: AdminUsersState, {pathname}) => {
    draft.pathname    = pathname;
  },
  [PRE_INITIALIZE_ADMIN_USERS] : (draft: AdminUsersState, {roles, accountId}: AdminUsersAction) => {
    draft.isPreInitialized = true;
    draft.roles = roles;
    if (roles && accountId != null) {
      draft.rolesByAccountId[accountId] = roles;
    }
  },
  [INITIALIZE_ADMIN_USERS] : (draft: AdminUsersState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, users}: AdminUsersState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : users.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminUsersState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_USERS_GRID_REFRESHED] : ({add, edit, destroy, bulkDelete, bulkEdit, bulkImport}: AdminUsersState) => {
    add.result = null;
    edit.result = null;
    destroy.result = null;
    bulkDelete.result = null;
    bulkEdit.result = null;
    bulkImport.result = null;
  },

  [GET_ADMIN_USERS_REQUEST] : ({users}: AdminUsersState) => {
    users.loading = true;
  },
  [GET_ADMIN_USERS_RESPONSE] : (
    {users, rolesByAccountId}: AdminUsersState,
    {rolesByAccountId: fetchedRolesByAccountId}: AdminUsersAction
  ) => {
    users.loading = false;
    Object.assign(rolesByAccountId, fetchedRolesByAccountId);
  },
  [GET_ADMIN_USERS_ERROR] : ({users}: AdminUsersState, {error}: AdminUsersAction) => {
    users.loading = false;
    users.error = error;
  },

  [IMPORT_ADMIN_USERS] : ({bulkImport}: AdminUsersState, {show, result}: AdminUsersAction) => {
    bulkImport.isImporting = show;
    bulkImport.result = result;
  },

  [ADD_ADMIN_USER] : ({add}: AdminUsersState, {show}: AdminUsersAction) => {
    add.isEditing = show;
    add.result = null;
  },
  [ADD_ADMIN_USER_REQUEST] : ({add}: AdminUsersState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_ADMIN_USER_RESPONSE] : ({add}: AdminUsersState, {user}: AdminUsersAction) => {
    add.loading = false;
    add.isEditing = false;
    add.result = user;
  },
  [ADD_ADMIN_USER_ERROR] : ({add}: AdminUsersState, {error}: AdminUsersAction) => {
    add.loading = false;
    add.error = error;
  },

  [EDIT_ADMIN_USER] : ({edit}: AdminUsersState, {user, show, isCurrentUser, isView}: AdminUsersEditAction) => {
    edit.selected      = show ? user : null;
    edit.isEditing     = show;
    edit.isView        = isView;
    edit.isCurrentUser = isCurrentUser;
    edit.result        = null;
  },
  [EDIT_ADMIN_USER_REQUEST] : ({edit}: AdminUsersState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_ADMIN_USER_RESPONSE] : ({edit}: AdminUsersState, {user}: AdminUsersAction) => {
    edit.loading = false;
    edit.isEditing = false;
    edit.isView = false;
    edit.result = user;
  },
  [EDIT_ADMIN_USER_ERROR] : ({edit}: AdminUsersState, {error}: AdminUsersAction) => {
    edit.loading = false;
    edit.error = error;
  },

  [DELETE_ADMIN_USER] : ({destroy}: AdminUsersState, {user, show}: AdminUsersAction) => {
    destroy.selected = show ? user : null;
    destroy.isEditing = show;
    destroy.result = null;
  },
  [DELETE_ADMIN_USER_REQUEST] : ({destroy}: AdminUsersState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_ADMIN_USER_RESPONSE] : ({destroy}: AdminUsersState, {user}: AdminUsersAction) => {
    destroy.loading = false;
    destroy.isEditing = false;
    destroy.result = user;
  },
  [DELETE_ADMIN_USER_ERROR] : ({destroy}: AdminUsersState, {error}: AdminUsersAction) => {
    destroy.loading = false;
    destroy.error = error;
    destroy.result = null;
  },

  [AUDIT_ADMIN_USER] : ({audit}: AdminUsersState, {user, show}: AdminUsersAction) => {
    audit.selected = show ? user : null;
    audit.isEditing = show;
    audit.result = null;
  },
  [AUDIT_ADMIN_USER_REQUEST] : ({audit}: AdminUsersState) => {
    audit.loading = true;
    audit.error = audit.result = null;
  },
  [AUDIT_ADMIN_USER_RESPONSE] : ({audit}: AdminUsersState, {result}: AdminUsersAction) => {
    audit.loading = false;
    audit.isEditing = false;
    audit.result = result;
  },
  [AUDIT_ADMIN_USER_ERROR] : ({audit}: AdminUsersState, {error}: AdminUsersAction) => {
    audit.loading = false;
    audit.error = error;
  },
  [AUDIT_ADMIN_USER_SHOW_CHANGES_DIALOG] : ({audit}: AdminUsersState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord = auditRecord;
  },

  [EDIT_ADMIN_USERS] : ({bulkEdit}: AdminUsersState, {users, show}: AdminUsersAction) => {
    bulkEdit.selected = show ? users : null;
    bulkEdit.isEditing = show;
    bulkEdit.result = null;
  },
  [EDIT_ADMIN_USERS_REQUEST] : ({bulkEdit}: AdminUsersState) => {
    bulkEdit.loading = true;
    bulkEdit.error = bulkEdit.result = null;
  },
  [EDIT_ADMIN_USERS_RESPONSE] : ({bulkEdit}: AdminUsersState, {users}: AdminUsersAction) => {
    bulkEdit.loading = false;
    bulkEdit.isEditing = false;
    bulkEdit.result = users;
  },
  [EDIT_ADMIN_USERS_ERROR] : ({bulkEdit}: AdminUsersState, {error}: AdminUsersAction) => {
    bulkEdit.loading = false;
    bulkEdit.error = error;
  },

  [MOVE_ADMIN_USERS] : ({move}: AdminUsersState, {users, show}: AdminUsersAction) => {
    move.selected  = show ? users : null;
    move.isEditing = show;
    move.result    = null;
  },
  [MOVE_ADMIN_USERS_REQUEST] : ({move}: AdminUsersState) => {
    move.loading = true;
    move.error   = move.result = null;
  },
  [MOVE_ADMIN_USERS_RESPONSE] : ({move}: AdminUsersState, {users, errors}: AdminUsersAction) => {
    move.loading   = false;
    move.isEditing = false;
    move.result    = users;
    move.errors    = errors;
  },
  [MOVE_ADMIN_USERS_ERROR] : ({move}: AdminUsersState, {error}: AdminUsersAction) => {
    move.loading = false;
    move.error   = error;
  },
  [SHOW_MOVED_USERS] : ({move}: AdminUsersState, {show}: AdminUsersAction) => {
    move.viewMovedUsers = show;
  },

  [DELETE_ADMIN_USERS] : ({bulkDelete}: AdminUsersState, {users, show}: AdminUsersAction) => {
    bulkDelete.selected = show ? users : null;
    bulkDelete.isEditing = show;
    bulkDelete.result = null;
    bulkDelete.record = null;
  },
  [DELETE_ADMIN_USERS_REQUEST] : ({bulkDelete}: AdminUsersState) => {
    bulkDelete.loading = true;
    bulkDelete.error = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_USERS_RESPONSE] : ({bulkDelete}: AdminUsersState, {users}: AdminUsersAction) => {
    bulkDelete.loading = false;
    bulkDelete.isEditing = false;
    bulkDelete.result = users;
    bulkDelete.record = users;
  },
  [DELETE_ADMIN_USERS_ERROR] : ({bulkDelete}: AdminUsersState, {error}: AdminUsersAction) => {
    bulkDelete.loading = false;
    bulkDelete.error = error;
  },
  [UPDATE_USERS_SEARCH_QUERIES] : ({search}: AdminUsersState, {searchParams}) => {
    search.id = searchParams.id || null;
    search.status = searchParams.status || null;
    search['role.name'] = searchParams?.['role.name'] || null;
    search.username = searchParams.username || null;
    search.firstName = searchParams.firstName || null;
    search.lastName = searchParams.lastName || null;
    search.description = searchParams.description || null;
  },
  [RESET_USERS_SEARCH_QUERIES] : ({search}: AdminUsersState) => {
    search.id = null;
    search.status = null;
    search['role.name'] = null;
    search.username = null;
    search.firstName = null;
    search.lastName = null;
    search.description = null;
  },
  [USER_PASSWORD_CHANGE] : ({changePassword}: AdminUsersState, {show}: AdminUsersAction) => {
    changePassword.showDialog = show;
  },
  [USER_PASSWORD_SAVE] : ({savePassword}: AdminUsersState, {userPassword}) => {
    savePassword.userPassword = userPassword;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: AdminUsersState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: AdminUsersState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: AdminUsersState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
